import Accordion from "@/components/Accordion/Accordion"
import { selectableFamilies } from "@/content/families"
import { cardsSummary, familiesSummary, RULES_COPY } from "@/content/sessionRules"
import type { SessionConfig } from "@/types"
import styles from "@/styles/components/SessionRules.module.css"

/** Which families are dealt. Nothing selected means nothing to
 * play, which is what blocks the session from starting. */
interface FamilyPickerProps {
  config: SessionConfig
  open: boolean
  onToggle: () => void
  /** One family in or out. Never the whole array — see `setFamilyInPlay`. */
  onPick: (family: number, inPlay: boolean) => void
  /**
   * Refuse to untick the last family. Set mid-session, where an empty deck is
   * not a rule waiting to be finished but a round nobody can play: the deal has
   * nothing to hand out, and the facilitator is blocked by "Nobody has picked a
   * card yet" with nothing to pick. In the lobby it stays unset — an empty deck
   * there is a legible half-made plan, and the start button says so.
   */
  lockLast?: boolean
}

export default function FamilyPicker({
  config,
  open,
  onToggle,
  onPick,
  lockLast = false,
}: FamilyPickerProps) {
  const locked = lockLast && config.families.length === 1

  return (
    <section className={styles.block}>
      <span className={styles.label}>{RULES_COPY.families}</span>

      <Accordion
        title={familiesSummary(config)}
        summary={cardsSummary(config)}
        open={open}
        onToggle={onToggle}
      >
        {/* `selectableFamilies`, not `familiesInPlay()`: printed numbering (what the
            facilitator ticks off a physical box), not deal order. It hides a retired
            family unless THIS session already plays it — a new session cannot add
            Monitors, an older one that has it still shows the tick it can untick —
            and it is the same set `familiesSummary` counts against, so the summary
            and the rows can never disagree. */}
        <div className={styles.familyList}>
          {selectableFamilies(config.families).map((family) => {
            const on = config.families.includes(family.n)
            const held = locked && on
            return (
              <button
                key={family.n}
                type="button"
                role="checkbox"
                aria-checked={on}
                className={styles.family}
                disabled={held}
                title={held ? RULES_COPY.lastFamily : family.name}
                onClick={() => onPick(family.n, !on)}
              >
                <span className={on ? `${styles.box} ${styles.boxOn}` : styles.box}>
                  {on ? "✓" : ""}
                </span>
                <span
                  className={styles.dot}
                  style={{ background: `var(--family-${family.n}-bloom)` }}
                />
                <span
                  className={on ? `${styles.familyName} ${styles.familyOn}` : styles.familyName}
                >
                  {family.short}
                </span>
                <span className={styles.meta}>{family.cardIds.length}</span>
              </button>
            )
          })}
        </div>

        {locked ? <p className={styles.hint}>{RULES_COPY.lastFamily}</p> : null}
      </Accordion>
    </section>
  )
}
